/**
 * @typedef BackInStockAttributes
 *
 * @property {boolean} [accepts_marketing]
 * @property {string} [phone_number] Required if email is null
 * @property {number} [quantity_required]
 */

/**
 * @typedef {Object} BackInStockSuccessResponse
 *
 * @property {'OK'} status
 * @property {string} message
 */

/**
 * @typedef {Object} BackInStockErrorResponse
 *
 * @property {'Error'} status
 * @property {Record<string, string[]>} errors
 */

class BackInStock {
  /**
   * @note It might be worth abstracting this further, for example simplifying the function signature
   * to have object containing email and phone number. That way this API could be easier to swap out
   * for other back ends in the future. It doesn't seem necessary at the moment though. - DW
   *
   * @param {string | null} email
   * @param {number} variantId
   * @param {number} productId
   * @param {BackInStockAttributes} [attributes]
   */
  static async subscribe (email, variantId, productId, attributes) {
    try {
      /** @type {BackInStockSuccessResponse | BackInStockErrorResponse} */
      const response = await window.BIS.create(email, variantId, productId, attributes);

      if (response.status === 'Error') {
        response.message = response?.errors?.base?.length
          ? response.errors.base.join(' ')
          : (response.message || 'Something went wrong. Please try again.');
      }
      return response;
    } catch (err) {
      /** Network/JS error surface */
      return { status: 'Error', message: 'Could not submit your request. Please try again.' };
    }
  }
}

class BackInStockForm extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['form', 'state', 'message']
  }

  setupListeners () {
    super.setupListeners();

    this.listeners.add(this.els.form.element, 'submit', this.handleFormSubmit.bind(this));

    // Listen for your theme’s variant change
    this.listeners.add(document, 'variant:change', (e) => {
      const id = e?.detail?.variant?.id;
      if (id) this.dataset.variantId = String(id);
    });
  }

  /** @param {SubmitEvent} event */
  handleFormSubmit (event) {
    event.preventDefault()

    this.message = ''

    this.subscribe()
  }

  async subscribe () {
    const fd = new FormData(this.els.form.element);
    const email = (fd.get('email') || '').toString().trim() || null;
    const phone = (fd.get('phone') || '').toString().trim() || null;

    if (!email && !phone) {
      this.state = 'idle';
      this.message = 'Please enter an email address or mobile number.';
      return;
    }

    const attrsBase = { accepts_marketing: !!fd.get('accepts_marketing') };

    // 1) Email signup (if present)
    let resp = { status: 'OK', message: '' };
    if (email) {
      resp = await BackInStock.subscribe(email, this.variantId, this.productId, attrsBase);
    }

    // 2) SMS signup (if present)
    if (resp.status === 'OK' && phone) {
      const smsAttrs = { ...attrsBase, phone_number: phone };
      // NOTE: email MUST be null for SMS
      resp = await BackInStock.subscribe(null, this.variantId, this.productId, smsAttrs);
    }

    this.state   = resp?.status?.toLowerCase() === 'ok' ? 'success' : 'idle';
    this.message = resp?.message || '';
  }

  /** @returns {'idle' | 'success'} */
  get state () {
    const visibleState = this.els.state.elements.find(
      (el) => el.getAttribute('aria-hidden') !== 'true'
    );
    return visibleState?.dataset.state || 'idle';
  }

  set state (state) {
    this.els.state.elements.forEach((el) => {
      el.setAttribute('aria-hidden', el.dataset.state !== state);
    });
  }

  get productId () {
    return Number(this.dataset.productId)
  }

  /** @todo unsure this updates as variant is changed */
  get variantId () {
    return Number(this.dataset.variantId)
  }

  get message () {
    return this._message
  }

  set message (message) {
    this._message = message

    this.els.message?.elements?.forEach((messageEl) => {
      messageEl.textContent = message
      messageEl.setAttribute('aria-hidden', !message?.length)
    })
  }
}

if (!window.customElements.get('back-in-stock-form')) {
  window.customElements.define('back-in-stock-form', BackInStockForm, {
    extends: 'aside',
  })
}
